import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { create } from 'zustand';
import { api, setConfirmRequiredHandler } from '@/api/client';
import { Modal } from '@/components/ui';
import { useAuth } from '@/stores/auth';

interface ConfirmItsYouState {
  /** `id` changes with every ask, so the dialog starts empty each time. */
  open: { id: number; resolve: (ok: boolean) => void } | null;
}

let asks = 0;

const useConfirmItsYou = create<ConfirmItsYouState>(() => ({ open: null }));

/**
 * Asks for the password (or a two-step code) again. Resolves true once the
 * server has accepted it; the request that asked is then sent again.
 */
export function askConfirmItsYou(): Promise<boolean> {
  return new Promise((resolve) => {
    useConfirmItsYou.getState().open?.resolve(false);
    useConfirmItsYou.setState({
      open: {
        id: ++asks,
        resolve: (ok) => {
          useConfirmItsYou.setState({ open: null });
          resolve(ok);
        },
      },
    });
  });
}

/**
 * "Confirm it's you". Some actions — resetting someone's password, changing
 * roles, saving the permission grid — must not be possible from a screen
 * someone left signed in. Once a sign-in is more than ten minutes old the
 * server asks for the password again, and this dialog answers it
 * (.docs/019). Mounted once, by App.
 */
export function ConfirmItsYouHost() {
  const open = useConfirmItsYou((s) => s.open);

  useEffect(() => {
    setConfirmRequiredHandler(askConfirmItsYou);
  }, []);

  if (!open) return null;
  return <ConfirmItsYouDialog key={open.id} resolve={open.resolve} />;
}

function ConfirmItsYouDialog({ resolve }: { resolve: (ok: boolean) => void }) {
  const twoStepOn = useAuth((s) => s.user?.twoStepEnabled ?? false);
  const [secret, setSecret] = useState('');
  const [byCode, setByCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cancel = useCallback(() => resolve(false), [resolve]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/auth/confirm', byCode ? { code: secret.trim() } : { password: secret });
      resolve(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Confirm it's you" onClose={cancel}>
      <form className="stack" onSubmit={submit}>
        <h2>Confirm it's you</h2>
        <p>
          This change needs your {byCode ? 'two-step code' : 'password'} again, in case this
          computer was left signed in.
        </p>
        <label className="field">
          <span className="field__label">{byCode ? '6-digit code from your phone' : 'Your password'}</span>
          <input
            className="field__input"
            type={byCode ? 'text' : 'password'}
            inputMode={byCode ? 'numeric' : undefined}
            autoComplete={byCode ? 'one-time-code' : 'current-password'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            required
          />
        </label>
        {twoStepOn && (
          <button
            type="button"
            className="linkbutton"
            onClick={() => {
              setByCode((v) => !v);
              setSecret('');
            }}
          >
            {byCode ? 'Use my password instead' : 'Use the code from my phone instead'}
          </button>
        )}
        {error && (
          <p className="login__error" role="alert">
            <span aria-hidden="true">⚠ </span>
            {error}
          </p>
        )}
        <div className="formactions">
          <button type="button" className="btn" onClick={cancel}>
            Cancel
          </button>
          <span className="formactions__spacer" />
          <button type="submit" className="btn btn--primary" disabled={busy || secret.length === 0}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
